import React, { useCallback, useEffect, useRef, useState } from 'react';
import Question from './Question';
import {
  Quiz,
  QUIZ_DATA_INITIAL_VALUE,
  Response,
  USER_RESPONSES_INITIAL_VALUE,
  UserResponses,
} from '../common';
import update from 'immutability-helper';
import AutosaveIndicator from './AutosaveIndicator';

interface Props {
  quizData?: string | null;
  responses?: string | null;
  onSaveUserResponses?: (userResponses: string) => void;
}
const QuizReader: React.FC<Props> = ({ quizData, responses, onSaveUserResponses }) => {
  const [quiz, setQuiz] = useState<Quiz | null>(QUIZ_DATA_INITIAL_VALUE);
  const [userResponses, setUserResponses] = useState<UserResponses | null>(USER_RESPONSES_INITIAL_VALUE);
  const hasEdited = useRef(false);
  const [autoSave, setAutoSave] = useState(false);

  const getQuestionResponse = useCallback(
    (questionId: string) => {
      return userResponses?.responses.find((response) => response.questionId === questionId)?.answer;
    },
    [userResponses?.responses],
  );

  const addUserResponses = useCallback((questionId: string, answer: Response['answer']) => {
    setUserResponses((prevUserResponses) =>
      update(prevUserResponses, {
        responses: {
          $push: [
            {
              questionId,
              answer,
            },
          ],
        },
      }),
    );
  }, []);

  const handleChangeResponse = useCallback(
    (questionId: string) => {
      const responseIndex = userResponses?.responses.findIndex(
        (response) => response.questionId === questionId,
      );
      return (answer: Response['answer']) => {
        setAutoSave(false);
        if (!hasEdited.current) {
          hasEdited.current = true;
        }
        if (responseIndex === -1 || responseIndex === undefined) {
          addUserResponses(questionId, answer);
        } else {
          setUserResponses((prevUserResponses) =>
            update(prevUserResponses, {
              responses: {
                [responseIndex]: {
                  answer: { $set: answer },
                },
              },
            }),
          );
        }
      };
    },
    [addUserResponses, userResponses?.responses, setAutoSave],
  );

  // hidrate quiz and response data
  useEffect(() => {
    if (!quizData) return;
    const quiz = JSON.parse(quizData);
    setQuiz(quiz);
  }, [quizData]);

  useEffect(() => {
    if (!responses) return;
    const userResponses = JSON.parse(responses);
    setUserResponses(userResponses);
  }, [responses]);

  // safe userResponses with debounce
  useEffect(() => {
    if (!userResponses || !onSaveUserResponses) return;
    if (!hasEdited.current) return;

    const debouncedUserResponses = setTimeout(() => {
      onSaveUserResponses(JSON.stringify(userResponses));
      setAutoSave(true);
    }, 500);
    return () => clearTimeout(debouncedUserResponses);
  }, [hasEdited, onSaveUserResponses, userResponses]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1, overflow: 'auto' }}>
      <AutosaveIndicator autoSave={autoSave} />
      {quiz &&
        quiz.questions.map((question, index) => (
          <Question
            key={question.id}
            id={question.id}
            type={question.type}
            question={question.question}
            options={question.options}
            response={getQuestionResponse(question.id)}
            onChangeResponse={handleChangeResponse(question.id)}
            config={question.config}
          />
        ))}
    </div>
  );
};

export default QuizReader;
